import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { LogoMark } from '../components/Logo';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useAuth } from '../state/AuthContext';
import { useT } from '../i18n';

const FOOTER_LINKS = [
  // The two public, un-gated pages. They live in the footer of every visitor's
  // first screen on purpose: internal linking is the structural half of SEO, and
  // a page nothing links to is a page nobody finds.
  { to: '/calculators', label: 'Calculators' },
  { to: '/licences', label: 'Licences' },
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms of Service' },
  { to: '/risk-disclosure', label: 'Risk Disclosure' },
  { to: '/refunds', label: 'Refunds & Cancellation' },
  { to: '/grievance', label: 'Grievance Redressal' },
  { to: '/disclaimer', label: 'Disclaimer' },
  { to: '/support', label: 'Support' },
  { to: '/delete-account', label: 'Delete Account' },
];

const REGISTER = [
  { asset: 'Indian equity', note: '7 stocks · via Zerodha', value: '₹20,29,709', pct: 42 },
  { asset: 'Mutual funds', note: 'from your CAS statement', value: '₹13,53,139', pct: 28 },
  { asset: 'Real estate', note: '1 property · manual entry', value: '₹8,69,875', pct: 18 },
  { asset: 'Gold & fixed deposits', note: '3 holdings', value: '₹5,79,917', pct: 12 },
];

// The Sansara promise — affirmations only. Positive by design (no struck-through "cuts").
const PROMISES = [
  { title: 'We guide, you decide', body: 'Facts and education, never tips. Every call on your money is yours to make.' },
  { title: 'Your hands on the reins', body: 'Nothing is ever automatic. You review and confirm every single move.' },
  { title: 'Facts over hype', body: 'Screen the whole market by the numbers — no “AI picks,” no noise, no FOMO.' },
  { title: 'One honest subscription', body: 'No hidden commissions, no selling your data, no touts paid to reach you.' },
  { title: 'Your attention, respected', body: 'No dark patterns. No manufactured urgency. Your focus stays yours.' },
  { title: 'Locked until licensed', body: 'Anything that legally needs a SEBI licence stays switched off — until we hold it.' },
];

function WheelMotif({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 180 180" fill="none" className={className} aria-hidden="true">
      <circle cx="90" cy="90" r="78" stroke="currentColor" strokeWidth="1" />
      <circle cx="90" cy="90" r="58" stroke="currentColor" strokeWidth="1" />
      <circle cx="90" cy="90" r="9" stroke="currentColor" strokeWidth="1" />
      <g stroke="currentColor" strokeWidth="1">
        <line x1="90" y1="12" x2="90" y2="168" />
        <line x1="12" y1="90" x2="168" y2="90" />
        <line x1="35" y1="35" x2="145" y2="145" />
        <line x1="145" y1="35" x2="35" y2="145" />
        <g transform="rotate(22.5 90 90)">
          <line x1="90" y1="12" x2="90" y2="168" />
          <line x1="12" y1="90" x2="168" y2="90" />
          <line x1="35" y1="35" x2="145" y2="145" />
          <line x1="145" y1="35" x2="35" y2="145" />
        </g>
      </g>
    </svg>
  );
}

export default function Landing() {
  const { session } = useAuth();
  const t = useT();
  const startHref = session ? '/app' : '/auth?mode=signup';

  return (
    <div className="overflow-x-hidden bg-surface-sunken text-ink dark:bg-surface-dark-sunken dark:text-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-stone-200/70 bg-surface-sunken/85 backdrop-blur dark:border-stone-800 dark:bg-surface-dark-sunken/85">
        <div className="mx-auto flex max-w-shell items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" aria-label={t('Sansara home')} className="flex items-center gap-3">
            <LogoMark size={38} />
            <span className="flex flex-col leading-none">
              <span className="font-display text-lg font-bold tracking-tight">Sansara</span>
              <span className="mt-1 hidden font-mono text-xs uppercase tracking-[0.16em] text-ink-faint dark:text-slate-400 sm:block">
                {t('Your eternal charioteer')}
              </span>
            </span>
          </Link>
          <nav className="flex items-center gap-6 text-sm text-ink-soft dark:text-slate-400">
            <a href="#why" className="hidden hover:text-ink dark:hover:text-slate-100 sm:inline">{t('Why Sansara')}</a>
            <a href="#promise" className="hidden hover:text-ink dark:hover:text-slate-100 sm:inline">{t('Promise')}</a>
            {/* The two PUBLIC pages, in the header as well as the footer. A page
                nothing links to is a page nobody finds — and /calculators is the
                whole top of the funnel, so it does not live only at the bottom
                of a long marketing page. Both are also in FOOTER_LINKS above, so
                a phone (where these are hidden, like their siblings) still has a
                way to them. */}
            <Link to="/calculators" className="hidden hover:text-ink dark:hover:text-slate-100 sm:inline">{t('Calculators')}</Link>
            <Link to="/licences" className="hidden hover:text-ink dark:hover:text-slate-100 sm:inline">{t('Licences')}</Link>
            {session ? (
              <Link to="/app" className="btn-primary rounded-full">{t('Open app')} <ArrowRight size={16} /></Link>
            ) : (
              <>
                {/* NOT `hidden sm:inline` like the two marketing anchors above.
                    Measured on the live site at 390px: this was the ONLY link to
                    /auth, and it was display:none below 640px — so every route out
                    of the landing page on a phone went to /auth?mode=signup, and a
                    RETURNING user was shown "Create your account" with an INVITE
                    CODE field on a product they already pay for. The signup page
                    does carry "Already have an account? Sign in", so it was
                    recoverable in one more tap — but the biggest and unhappiest
                    cohort in the sim is mobile, and this is their front door. The
                    marketing anchors may stay hidden; the way back in may not. */}
                {/* Conv-57 R7 — the signed-out front door is where a
                    non-English speaker decides whether the app is for them.
                    The switcher works with no session (it writes the on-device
                    override), so it belongs here as much as in the app shell. */}
                <LanguageSwitcher compact />
                <Link to="/auth" className="hover:text-ink dark:hover:text-slate-100">{t('Sign in')}</Link>
                <Link to="/auth?mode=signup" className="btn-primary rounded-full">{t('Get started')}</Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -right-[4vw] -top-[3vw] select-none font-serif text-[34vw] leading-none text-brand-600/[0.05] dark:text-brand-400/[0.06]"
        >
          सं
        </span>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(52rem 26rem at 22% -8%, rgba(138,102,38,0.12), transparent 60%)' }}
        />
        {/* The image column is the wider of the two: the shot is ~2.35:1, so at the
            old 0.95fr it rendered as a thin strip beside a tall text column. It stays
            INSIDE the shell — bleeding it past the edge clipped the third total card,
            and a product shot cut mid-number reads as a rendering fault, which is the
            opposite of what a screenshot is here to prove. */}
        <div className="relative mx-auto grid max-w-shell items-center gap-14 px-5 pb-24 pt-10 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-10">
          <div>
            <span className="chip border border-brand-200 bg-brand-50 font-mono text-xs uppercase tracking-[0.14em] text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300">
              {t('Built for anyone who wants to own their worth')}
            </span>
            <h1 className="mt-5 font-serif text-5xl font-semibold leading-[0.99] tracking-tight sm:text-[68px]">
              {t('Hold the reins.')}<br />
              {t('We’ll')} <em className="not-italic text-brand-700 dark:text-brand-400">{t('light the road')}</em>.
            </h1>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-ink-soft dark:text-slate-400">
              {t('Like a charioteer, Sansara lights the road through the market with facts — and hands you every decision. Track everything you own. No tips, no autopilot.')}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6">
              <Link to={startHref} className="btn-primary rounded-full !px-6 !py-3 !text-base">
                {t('Start free — keep it free')} <ArrowRight size={18} />
              </Link>
              <a href="#why" className="border-b-2 border-brand-600 pb-0.5 text-base font-semibold text-brand-700 dark:text-brand-400">
                {t('See how it works')}
              </a>
            </div>
            <p className="mt-4 text-sm text-ink-faint dark:text-slate-400">
              {/* Conv-56: the Free plan is the floor and never expires, so the
                  caption can promise exactly what is true — nothing to pay, no
                  card, no clock. */}
              {t('Free forever. No card needed.')}
            </p>
          </div>

          {/* The real product, angled and node-framed to match the palette rather than
              pasted in as a flat rectangle. Two files, not one: a light screenshot on a
              dark page (or the reverse) reads as a foreign object, so each theme gets
              its own capture and CSS picks. Both are shots of the Platinum dashboard on
              SANDBOX data — the "Simulated market prices" badge is visible inside the
              image, so the disclosure travels with the picture and cannot be cropped
              off, and the caption below repeats it in text for anyone who cannot see
              the image at all. */}
          <div className="relative [transform:rotate(-1.4deg)]">
            {['-left-1.5 -top-1.5', '-right-1.5 -top-1.5', '-bottom-1.5 -left-1.5', '-bottom-1.5 -right-1.5'].map((pos) => (
              <span key={pos} className={`absolute ${pos} z-10 h-3 w-3 rounded-full bg-brand-600 dark:bg-brand-400`} />
            ))}
            <div className="card overflow-hidden [transform:rotate(1.4deg)] p-0 shadow-pop">
              {/* The files are already cropped to the band that carries the message —
                  nav, the three totals, the disclosure lines, the index row — so the
                  markup does no cropping of its own. Re-shooting only means replacing
                  two files at the same aspect; nothing here needs to change. */}
              <div className="relative">
                <img
                  src="/hero-dashboard-light.webp"
                  alt={t('The Sansara dashboard, showing net worth, invested value and unrealised profit across every asset class, with Indian index levels below.')}
                  className="block h-auto w-full dark:hidden"
                  width={1280}
                  height={545}
                  decoding="async"
                />
                <img
                  src="/hero-dashboard-dark.webp"
                  /* Same alt as the light variant, NOT aria-hidden: whichever image is
                     inactive is `display:none` and therefore already out of the
                     accessibility tree, so this is never announced twice — whereas
                     hiding it left the hero with no accessible name at all in dark. */
                  alt={t('The Sansara dashboard, showing net worth, invested value and unrealised profit across every asset class, with Indian index levels below.')}
                  className="hidden h-auto w-full dark:block"
                  width={1280}
                  height={545}
                  decoding="async"
                />
                {/* A hairline inside the frame so the screenshot's own near-white /
                    near-black edge does not butt straight against the card border. */}
                <span aria-hidden="true" className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-stone-900/5 dark:ring-white/10" />
              </div>
              {/* Sentence case, normal sans — NOT mono/caps/tracked. This line
                  used to render as "ILLUSTRATIVE — SANDBOX PRICES, SAMPLE
                  HOLDINGS", i.e. hazard-label typography on the first thing a
                  visitor sees, and it read as a warning about the product rather
                  than a fact about the picture. The alarm was in the CSS, not the
                  vocabulary. What it must NOT lose is the claim: this hero is a
                  screenshot of a demo account on SANDBOX prices
                  (`LIVE_FEED_IMPLEMENTED = false` makes `isSandboxData()` true in
                  every configuration, so there is no future in which these
                  figures become real), and this caption is the ONLY text-layer
                  statement on the whole landing page that the ₹3.29 Cr is
                  invented — the footer disclaimer is about SEBI registration and
                  says nothing about the numbers. Reword it freely; do not delete
                  it, and do not drop either half (prices AND holdings are
                  fictional). */}
              <p className="border-t border-stone-300/70 px-5 py-3 text-sm leading-relaxed text-ink-soft dark:border-stone-700/60 dark:text-slate-400">
                {t('The real Sansara screen, shown with a demo account — sample holdings, simulated prices.')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Register band — cinematic, one honest page */}
      <section
        id="why"
        className="relative bg-surface-dark text-slate-100 dark:bg-black"
        style={{ clipPath: 'polygon(0 46px, 100% 0, 100% 100%, 0 100%)' }}
      >
        <div className="mx-auto max-w-shell px-5 pb-24 pt-28 sm:px-8">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand-400">
            {t('One register for everything you own')}
          </span>
          <h2 className="mt-4 max-w-[20ch] font-serif text-4xl font-semibold leading-tight tracking-tight sm:text-[44px]">
            {t('Your')} <em className="not-italic text-brand-400">{t('whole')}</em> {t('net worth, on one honest page.')}
          </h2>
          <p className="mt-4 max-w-2xl leading-relaxed text-slate-400">
            {t('Stocks, mutual funds, gold, fixed deposits, bonds, property — even fractional real estate. Sansara adds it up plainly and shows you the facts. It never sells you a tip.')}
          </p>

          <div className="mt-10 border-t border-white/10">
            {REGISTER.map(({ asset, note, value, pct }) => (
              <div key={asset} className="grid grid-cols-[1.5fr_1fr] items-center gap-6 border-b border-white/10 py-5 sm:grid-cols-[1.5fr_1fr_1.7fr]">
                <div>
                  <p className="font-semibold">{t(asset)}</p>
                  <p className="mt-0.5 font-mono text-xs text-slate-400">{t(note)}</p>
                </div>
                <p className="num text-right font-bold">{value}</p>
                <div className="hidden h-1.5 overflow-hidden rounded-full bg-brand-400/15 sm:block">
                  <div className="h-full rounded-full bg-brand-400" style={{ width: `${pct}%` }} />
                </div>
              </div>
            ))}
            <div className="grid grid-cols-[1.5fr_1fr] items-center gap-6 pt-6 sm:grid-cols-[1.5fr_1fr_1.7fr]">
              <p className="font-serif text-xl">{t('Net worth')}</p>
              <p className="num text-right font-serif text-2xl text-brand-400">₹48,32,640</p>
              {/* Sentence case, not a mono/caps stamp — same reasoning as the hero
                  caption above. The figure beside it is invented, so the marker has to
                  stay; what it must not do is read like a compliance warning stapled to
                  the product. */}
              <span className="justify-self-start rounded border border-brand-400 px-2 py-0.5 text-xs text-brand-400">
                {t('Example figure')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* The Sansara promise — positive affirmations */}
      <section id="promise" className="relative overflow-hidden py-24">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none font-serif text-[28vw] leading-none text-brand-600/[0.04] dark:text-brand-400/[0.05]"
        >
          कर्तव्य
        </span>
        <div className="relative mx-auto max-w-shell px-5 sm:px-8">
          <span className="chip border border-brand-200 bg-brand-50 font-mono text-xs uppercase tracking-[0.14em] text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300">
            {t('The Sansara promise')}
          </span>
          <h2 className="mt-4 max-w-[16ch] font-serif text-4xl font-semibold leading-[1.02] tracking-tight sm:text-5xl">
            {t('What you can always')} <em className="not-italic text-brand-700 dark:text-brand-400">{t('count on')}</em>.
          </h2>
          <p className="mt-4 max-w-lg leading-relaxed text-ink-soft dark:text-slate-400">
            {t('Six commitments that shape every screen. Honest by design — and built to keep your trust.')}
          </p>

          <div className="mt-12 grid gap-x-12 sm:grid-cols-2">
            {PROMISES.map(({ title, body }, i) => (
              <div
                key={title}
                className={`grid grid-cols-[auto_1fr] gap-5 border-t border-stone-200 py-6 dark:border-stone-800 ${i > 1 ? 'sm:[&]:border-t' : ''}`}
              >
                <span className="font-serif text-2xl leading-none text-brand-700 dark:text-brand-400">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold tracking-tight">{t(title)}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-soft dark:text-slate-400">{t(body)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Close */}
      <section className="relative overflow-hidden border-t border-stone-200 bg-surface py-24 dark:border-stone-800 dark:bg-surface-dark">
        <WheelMotif className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 text-brand-600/[0.06] dark:text-brand-400/[0.07]" />
        <div className="relative mx-auto max-w-shell px-5 text-center sm:px-8">
          <h2 className="font-serif text-4xl font-semibold tracking-tight sm:text-5xl">
            {t('Be your own')} <em className="not-italic text-brand-700 dark:text-brand-400">{t('fund manager')}</em>.
          </h2>
          <p className="mx-auto mt-4 max-w-md leading-relaxed text-ink-soft dark:text-slate-400">
            {t('Start free. Track everything, learn everything — free costs nothing and never expires. Paid tiers add analytics and structure when you want them.')}
          </p>
          <Link to={startHref} className="btn-primary mt-8 rounded-full !px-7 !py-3.5 !text-base">
            {t('Start free — keep it free')} <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 py-12 dark:border-stone-800">
        <div className="mx-auto max-w-shell px-5 sm:px-8">
          <div className="flex flex-col justify-between gap-8 sm:flex-row">
            <div className="max-w-sm">
              <div className="flex items-center gap-3">
                <LogoMark size={36} />
                <span className="font-display text-lg font-bold tracking-tight">Sansara</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-ink-faint dark:text-slate-400">
                {t('Sansara (संसार) — the eternal charioteer, the cycle of the world. We light the road; the destination is always yours.')}
              </p>
            </div>
            <nav className="grid grid-cols-2 gap-x-10 gap-y-2.5" aria-label={t('Legal')}>
              {FOOTER_LINKS.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className="text-sm text-ink-soft transition-colors hover:text-brand-700 dark:text-slate-400 dark:hover:text-brand-400"
                >
                  {t(label)}
                </Link>
              ))}
            </nav>
          </div>
          <div className="mt-10 border-t border-stone-100 pt-6 text-xs leading-relaxed text-ink-faint dark:border-stone-800 dark:text-slate-400">
            <p>
              {t('© {year} Sansara · Kanpur, Uttar Pradesh, India. Sansara is not a SEBI-registered investment adviser or research analyst and does not provide buy/sell recommendations. Investments in securities markets are subject to market risks; read all related documents carefully before investing. Linked broker services are provided by the respective SEBI-registered brokers.', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
